import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import SeoHelmet from "./components/SeoHelmet";
import axios from "axios";
import { API_BASE_URL } from "../config";
import FloatingWidgets from "./components/FloatingWidgets";
import Select from "react-select";

export default function BulkOrder() {
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    eventType: "",
    requirements: "",
    agreement: false,
  });

  const [selectedCats, setSelectedCategories] = useState([]);
  const [selectedProds, setSelectedProducts] = useState([]);
  const [productQuantities, setProductQuantities] = useState({});

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/content/categories?status=Published`),
          axios.get(`${API_BASE_URL}/api/content/products?status=Published`),
        ]);
        setCategories(catRes.data?.data || []);
        setAllProducts(prodRes.data?.data || []);
      } catch (err) {
        console.error("Error fetching bulk form data", err);
      } finally {
        setDataLoading(false);
      }
    };
    fetchFormData();
  }, []);

  const filteredProducts = useMemo(() => {
    if (selectedCats.length === 0) return [];
    const catIds = selectedCats.map((c) => c.value);
    return allProducts.filter((p) => {
      const pCatId = typeof p.category === "object" ? p.category._id : p.category;
      return catIds.includes(pCatId);
    });
  }, [selectedCats, allProducts]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "60px",
      background: "var(--clr-bg-grey)",
      border: "0",
      borderRadius: "5px",
      padding: "0 10px",
      boxShadow: "none",
      "&:hover": {
        border: "0",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 10px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#707070",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#707070",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "rgba(167, 34, 0, 0.1)",
      borderRadius: "4px",
    }),
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Name is required";
    if (!formData.phone.match(/^\d{10}$/))
      newErrors.phone = "Valid 10-digit phone required";
    if (!formData.email.match(/\S+@\S+\.\S+/))
      newErrors.email = "Valid email required";
    if (!formData.eventType) newErrors.eventType = "Please select event type";
    if (selectedProds.length === 0)
      newErrors.products = "Please select at least one product";
    if (!formData.agreement)
      newErrors.agreement = "You must agree to be contacted";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleQtyChange = (productId, value) => {
    setProductQuantities((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    const finalPayload = {
      ...formData,
      items: selectedProds.map((p) => ({
        name: p.label,
        productId: p.value,
        quantity: productQuantities[p.value] || "",
      })),
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/bulk-order-enquiry`,
        finalPayload,
      );
      if (response.data.success) {
        setToast({
          type: "success",
          message: "Success! Our Bulk Manager will contact you shortly.",
        });
        setFormData({ fullName: "", phone: "", email: "", eventType: "", requirements: "", agreement: false });
        setSelectedCategories([]);
        setSelectedProducts([]);
        setProductQuantities({});
      }
    } catch (error) {
      setToast({
        type: "error",
        message: "Submission failed. Please call us directly at 9751336638.",
      });
    } finally {
      setLoading(false);
      setTimeout(() => setToast(null), 5000);
    }
  };

  return (
    <>
      <SeoHelmet
        title="Bulk Orders & Large Celebrations | A1 Bakery & Sweets"
        description="Madurai's most trusted partner for bulk sweets and snacks. Premium wedding seer, corporate gifting, and large-scale event catering since 1962."
      />
      <Navbar />

      <header className="header-about">
        <div className="header-content">
          <div className="breadcrumb__list mb-20">
            <span>
              <Link to="/">Home</Link>
            </span>
            <span className="dvdr">
              <i className="fa-regular fa-angle-right"></i>
            </span>
            <span>Bulk Order</span>
          </div>
          <h1>Every sweet tells a story. Every snack carries a memory</h1>
          <p>Every sweet tells a story. Every snack carries a memory.</p>
        </div>
      </header>

      <main>
        <section className="bg-white mt-40 mb-40">
          <div className="container">
            <div className="title-section text-center">
              <h2>Curated For Excellence</h2>
              <p>
                Tailored solutions for your most significant milestones.
              </p>
            </div>

            <div className="row g-0">
              {[
                {
                  icon: "fa-ring",
                  title: "Wedding Seer",
                  text: "Timeless tradition meets premium presentation for your 'Seer' plates.",
                },
                {
                  icon: "fa-building-columns",
                  title: "Executive Gifting",
                  text: "Sophisticated branded boxes that reflect your corporate values.",
                },
                {
                  icon: "fa-star-and-crescent",
                  title: "Festive Joy",
                  text: "Mass production with individual attention for Diwali, Eid & Pongal.",
                },
                {
                  icon: "fa-truck-fast",
                  title: "Global Logistics",
                  text: "Seamless delivery management for large-scale multi-location events.",
                },
              ].map((item, i) => (
                <div className="col-lg-3 col-md-6" key={i}>
                  <div className="bo-bespoke-card h-100">
                    <div className="bo-bespoke-icon-circle">
                      <i className={`fa-solid ${item.icon}`}></i>
                    </div>
                    <h4 className="fw-bold mb-3">{item.title}</h4>
                    <p className="text-muted small mb-0">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <section className="bg-light pt-50 pb-50">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-12">
              <div className="bo-v4-full-card">
                <div className="title-section text-center">
                  <h1>
                    Plan Your Large Celebration
                  </h1>
                  <p>
                    Share your vision with us. Our bulk experts will curate a
                    menu that matches your theme and guest profile perfectly.
                  </p>
                </div>
                <div className="bd-contact__form">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="bd-single__form-input mb-20">
                          <label className="bo-v4-label">Full Name *</label>
                          <input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="Enter your name"
                          />
                          {errors.fullName && (
                            <small className="text-danger">
                              {errors.fullName}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="bd-single__form-input mb-20">
                          <label className="bo-v4-label">Phone Number *</label>
                          <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="10 digit mobile number"
                          />
                          {errors.phone && (
                            <small className="text-danger">
                              {errors.phone}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="bd-single__form-input mb-20">
                          <label className="bo-v4-label">Email Address *</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="example@mail.com"
                            required
                          />
                          {errors.email && (
                            <small className="text-danger">
                              {errors.email}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="bd-single__form-input mb-20">
                          <label className="bo-v4-label">Occasion Type *</label>
                          <select
                            name="eventType"
                            value={formData.eventType}
                            onChange={handleChange}
                            className="w-100"
                            style={{
                              height: "60px",
                              background: "var(--clr-bg-grey)",
                              border: "0",
                              borderRadius: "5px",
                              padding: "0 20px",
                              color: "#707070",
                              appearance: "none",
                              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23707070' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E")`,
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "right 20px center",
                            }}
                          >
                            <option value="">Occasion Type</option>
                            <option value="Wedding">Wedding / Reception</option>
                            <option value="Corporate">Corporate Gifting</option>
                            <option value="Festival">
                              Festive Celebrations
                            </option>
                            <option value="Other">Other Events</option>
                          </select>
                          {errors.eventType && (
                            <small className="text-danger">
                              {errors.eventType}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="bd-single__form-input mb-20">
                          <label className="bo-v4-label">Select Categories *</label>
                          <Select
                            isMulti
                            options={categories.map((c) => ({
                              value: c._id,
                              label: c.name,
                            }))}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            onChange={(val) => {
                              setSelectedCategories(val || []);
                              setSelectedProducts([]); // Reset products when categories change
                            }}
                            value={selectedCats}
                            placeholder="Choose product categories..."
                            isLoading={dataLoading}
                            styles={selectStyles}
                          />
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="bd-single__form-input mb-20">
                          <label className="bo-v4-label">Select Products *</label>
                          <Select
                            isMulti
                            options={filteredProducts.map((p) => ({
                              value: p._id,
                              label: p.name,
                            }))}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            onChange={(val) => setSelectedProducts(val || [])}
                            value={selectedProds}
                            placeholder="Search products..."
                            isDisabled={selectedCats.length === 0}
                            styles={selectStyles}
                          />
                          {errors.products && (
                            <small className="text-danger d-block mt-1">{errors.products}</small>
                          )}
                        </div>
                      </div>

                      {selectedProds.length > 0 && (
                        <div className="col-md-12 mb-20">
                          <label className="bo-v4-label">Approximate Quantities per product</label>
                          <div className="row g-3">
                            {selectedProds.map((prod) => (
                              <div key={prod.value} className="col-md-6">
                                <div className="bo-v4-product-item">
                                  <span className="fw-bold text-dark">{prod.label}</span>
                                  <div className="bd-single__form-input m-0">
                                    <input
                                      type="text"
                                      className="bo-v4-qty-input"
                                      placeholder="Quantity"
                                      value={productQuantities[prod.value] || ""}
                                      onChange={(e) =>
                                        handleQtyChange(prod.value, e.target.value)
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="col-md-12">
                        <div className="bd-single__form-input">
                          <label className="bo-v4-label">Specific Requirements (Optional)</label>
                          <textarea
                            name="requirements"
                            value={formData.requirements}
                            onChange={handleChange}
                            placeholder="E.g. Custom packaging colors, specific delivery date, or special instructions..."
                          ></textarea>
                        </div>
                      </div>
                    </div>

                    <div className="checkbox mb-30 d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        id="agreement"
                        name="agreement"
                        checked={formData.agreement}
                        onChange={handleChange}
                        style={{ width: "auto", height: "auto" }}
                      />
                      <label
                        className="form-check-label fs-6 medium m-0"
                        htmlFor="agreement"
                      >
                        I authorize A1 Sweets to contact me with a custom
                        proposal.
                      </label>
                    </div>
                    {errors.agreement && (
                      <small className="text-danger d-block mb-2">
                        {errors.agreement}
                      </small>
                    )}

                    <div className="contact-btn text-center">
                      <button
                        type="submit"
                        className="bm-hero__cta px-5"
                        disabled={loading}
                      >
                        {loading ? "Syncing..." : "Send Request"}
                      </button>
                    </div>
                  </form>
                  {toast && (
                    <div
                      className={`alert mt-4 ${toast.type === "success" ? "alert-success" : "alert-danger"}`}
                    >
                      {toast.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWidgets />
    </>
  );
}
